"use client";

import { sumBy } from "es-toolkit";
import { useMemo, useState } from "react";
import { LuInfo } from "react-icons/lu";

import CoinPriceFormatter from "@/components/shared/coin-price-formatter";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import useAmmOrderbook from "@/hooks/use-amm-orderbook";
import { type CoinPropertiesResponse } from "@/hooks/use-coin-properties-query";
import { DESO_APP_NAME, OPENFUND_APP_NAME } from "@/utils/constants";
import {
  basisPointsToPercent,
  focusToUsd,
  formatDecimalValue,
  getVisiblePrice,
} from "@/utils/currency";
import { abbreviateNumber } from "@/utils/helpers";

import { LearnMore } from "../learn-more-link";
import AmmSummary from "../trade/amm/amm-summary";
import AmmSummaryGraph from "../trade/amm/amm-summary-graph";
import TokenOrderbook, { OrderSide } from "../trade/amm/token-orderbook";

interface TokenPostLaunchAmmProps {
  tokenDetails: CoinPropertiesResponse;
  coinPublicKey: string;
  focusExchangeRate: number;
  desoExchangeRate: number;
}

const TokenPostLaunchAmm = ({
  tokenDetails,
  coinPublicKey,
  focusExchangeRate,
  desoExchangeRate,
}: TokenPostLaunchAmmProps) => {
  const [showInfo, setShowInfo] = useState(false);
  const tokenPriceUsd = tokenDetails?.coinPrice
    ? getVisiblePrice(tokenDetails.coinPrice)
    : 0;

  const { ammOrderbook, loadingAmmOrderbook } = useAmmOrderbook({
    publicKey: coinPublicKey,
    tokenPriceUsd,
    focusExchangeRate,
    desoExchangeRate,
    transactorPublicKeyBase58Check:
      tokenDetails.coinProperties.AmmConfigs?.[0]?.AmmPublicKey || "",
  });

  const ammConfig = tokenDetails?.coinProperties.AmmConfigs?.length
    ? tokenDetails?.coinProperties.AmmConfigs[0]
    : null;

  const reversedAmmOrderbook = useMemo(() => {
    return [
      ...(ammOrderbook?.asks || []),
      ...(ammOrderbook?.bids || []),
    ].reverse();
  }, [ammOrderbook]);

  const renderAmmSummary = () => {
    if (!ammConfig || !reversedAmmOrderbook.length) {
      return null;
    }

    const config = [
      {
        title: "Start Price (USD)",
        value: <CoinPriceFormatter price={ammConfig.StartPriceUsd} />,
        key: "startPriceUsd",
      },
      {
        title: "Order Spacing",
        value:
          formatDecimalValue(
            basisPointsToPercent(ammConfig.OrderSpacingBasisPoints),
          ) + "%",
        key: "orderSpacingPercentage",
      },
      {
        key: "baseAmountPerLevelUsd",
        value: <CoinPriceFormatter price={ammConfig.BaseAmountPerLevelUsd} />,
        title: "Amount Per Level USD",
      },
      {
        key:
          ammConfig.IncreaseBaseAmountByUsdEachLevel > 0
            ? "increaseBaseAmountByUsdEachLevel"
            : "increaseBaseAmountByBasisPointsEachLevel",
        value:
          ammConfig.IncreaseBaseAmountByUsdEachLevel > 0 ? (
            <CoinPriceFormatter
              price={ammConfig.IncreaseBaseAmountByUsdEachLevel}
              maxDecimalsDigitsAfterZero={4}
            />
          ) : (
            formatDecimalValue(
              basisPointsToPercent(
                ammConfig.IncreaseBaseAmountByBasisPointsEachLevel,
              ),
            ) + "%"
          ),
        title:
          ammConfig.IncreaseBaseAmountByUsdEachLevel > 0
            ? "Amount Increase by USD"
            : "Amount Increase Per Level",
      },
      {
        key: "terminalPriceUsd",
        value: !ammConfig.TerminalPriceUsd ? (
          <div className="inline-block font-mono text-muted-foreground">
            &mdash;
          </div>
        ) : (
          <CoinPriceFormatter price={ammConfig.TerminalPriceUsd} />
        ),
        title: "Terminal Price USD",
      },
      {
        key: "terminalAmountUsd",
        value: !ammConfig.TerminalAmountUsd ? (
          <div className="inline-block font-mono text-muted-foreground">
            &mdash;
          </div>
        ) : (
          <CoinPriceFormatter price={ammConfig.TerminalAmountUsd} />
        ),
        title: "Terminal Amount USD",
      },
      {
        key: "finalPriceUsd",
        value: !ammConfig.FinalPriceUsd ? (
          <div className="inline-block font-mono text-muted-foreground">
            &mdash;
          </div>
        ) : (
          <CoinPriceFormatter price={ammConfig.FinalPriceUsd} />
        ),
        title: "Final Price USD",
      },
    ];

    const totalTokensInBids = sumBy(ammOrderbook?.bids || [], (level) => {
      return level.AmountInQuoteCurrency;
    });

    const totalAmountInAsks = sumBy(ammOrderbook?.asks || [], (level) => {
      return level.AmountInQuoteCurrency;
    });

    const totalTokensInAsks = sumBy(ammOrderbook?.asks || [], (level) => {
      return level.AmountInQuoteCurrency / level.PriceInQuoteCurrency;
    });

    return (
      <AmmSummary
        ammConfig={config}
        orders={reversedAmmOrderbook}
        coinUsername={tokenDetails.coinProperties.Profile.Username}
        renderTotalAmount={() => (
          <>
            <div className="flex flex-col gap-1">
              <div className="text-xs text-green-500">Total Amount in Bid</div>
              <CoinPriceFormatter
                price={focusToUsd(totalTokensInBids, focusExchangeRate)}
                className="font-mono text-base text-green-500"
              />
              <div className="text-xs text-muted">
                <span className="flex flex-nowrap items-start gap-1 font-mono">
                  {abbreviateNumber(totalTokensInBids)}

                  <span className="truncate wrap-break-word">
                    ${tokenDetails.coinProperties.Profile.Username}
                  </span>
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <div className="text-xs text-red-500">Total Amount in Ask</div>
              <CoinPriceFormatter
                price={focusToUsd(totalAmountInAsks, focusExchangeRate)}
                className="font-mono text-base text-red-500"
              />
              <div className="text-xs text-muted">
                <span className="flex flex-nowrap items-start gap-1 font-mono">
                  {abbreviateNumber(totalTokensInAsks)}

                  <span className="truncate wrap-break-word">
                    ${tokenDetails.coinProperties.Profile.Username}
                  </span>
                </span>
              </div>
            </div>
          </>
        )}
      />
    );
  };

  if (loadingAmmOrderbook) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-[355px] w-full" />
        <Skeleton className="h-[315px] w-full" />
        <Skeleton className="h-[400px] w-full" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <div className="mb-4 flex items-center gap-1">
          <h2
            className="
            font-sans text-base font-semibold text-muted-foreground
          "
          >
            AMM Progress
          </h2>
          <Button
            variant="link"
            onClick={() => setShowInfo(!showInfo)}
            className="
              flex items-center gap-2 text-xs text-muted underline-offset-2
              transition-colors
              hover:text-muted-foreground hover:underline
            "
          >
            How it works
            <LuInfo className="text-lg text-muted" />
          </Button>
        </div>

        {showInfo && (
          <div
            className="
              animate-in space-y-4 rounded-2xl border p-6 text-sm text-muted
              duration-700 fade-in
            "
          >
            <p>
              These are the initial orders that the Automated Market-Maker (AMM)
              will place to initialize the market for this token.
            </p>

            <p>
              Whenever a sell order fills, the AMM places a buy order on the
              other side, and vice-versa, so there's always liquidity for buyers
              and sellers.
            </p>

            <p>
              Before the market opens, anyone can place a bid to reserve tokens
              at the lowest possible price, including you! Bids will be filled
              by the AMM's sell orders when it starts.
            </p>

            <p>
              All orders are fully on-chain thanks to the {DESO_APP_NAME}{" "}
              blockchain, and you can see the full market and place on-chain
              limit orders via the {OPENFUND_APP_NAME} client. And yes, order
              matching is fully on-chain too!
            </p>

            <p>
              <LearnMore />
            </p>
          </div>
        )}
      </div>

      <AmmSummaryGraph
        orders={reversedAmmOrderbook}
        graphOptions={{
          chart: {
            stacked: false,
            zoom: { enabled: false },
          },
          stroke: {
            curve: "step",
            width: 2,
          },
          fill: {
            type: "gradient",
            gradient: {
              shadeIntensity: 1,
              inverseColors: false,
              opacityFrom: 0.45,
              opacityTo: 0.05,
              stops: [20, 100, 100, 100],
            },
          },
          yaxis: {
            forceNiceScale: true,
            title: {
              text: "Token Price USD",
              style: { color: "#fff" },
            },
          },
          xaxis: {
            title: {
              text: "Token Amount",
              style: { color: "#fff" },
            },
          },
          colors: ["#22c55d", "#ef4444"],
        }}
        delimiters={[tokenPriceUsd]}
        coinUsername={tokenDetails.coinProperties.Profile.Username}
        chunkTitles={["Tokens Purchased", "Unsold AMM Tokens"]}
      />

      <div className="mb-4">{renderAmmSummary()}</div>
      {!loadingAmmOrderbook && (
        <>
          <h2
            className="
              mb-4 font-sans text-base font-semibold text-muted-foreground
            "
          >
            AMM Orders
          </h2>

          <TokenOrderbook
            orders={ammOrderbook?.asks || []}
            coinUsername={tokenDetails.coinProperties.Profile.Username}
            coinPublicKey={
              tokenDetails.coinProperties.Profile.PublicKeyBase58Check
            }
          />

          <div
            className="
              my-4 flex w-full items-center justify-center gap-2 text-sm
              text-muted
            "
          >
            Mid Price:
            <CoinPriceFormatter
              price={tokenPriceUsd}
              className="font-mono text-sm text-muted-foreground"
            />
          </div>

          <div>
            <TokenOrderbook
              orders={ammOrderbook?.bids || []}
              coinUsername={tokenDetails.coinProperties.Profile.Username}
              coinPublicKey={
                tokenDetails.coinProperties.Profile.PublicKeyBase58Check
              }
              wrapperClassName="mb-4"
              side={OrderSide.BID}
              showTitles={false}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default TokenPostLaunchAmm;
